import { ChangeEvent, useState, useEffect, useRef, KeyboardEvent } from "react";
import { BiSearch } from "react-icons/bi";
import { Flex, Box } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilValue, useRecoilState } from "recoil";

import { NoResults } from "./NoResults";
import { DropdownItem } from "./DropdownItem";
import {
  useSuggestedMatchHistoryUsers,
  useUserSuggestionsForSearch,
} from "MVVM/Hooks/useSuggestedUsers";
import { analytics } from "MVVM/Analytics";
import { AllstarUser } from "MVVM/State";
import { MatchHistorySearchBarIsOpenState } from "MVVM/State/matchHistory";

export interface ISearchItem {
  id: string;
  name: string;
  image: string;
  gameId?: string;
  reason?: string;
  userGroup?: {
    type: string;
  };
  rank?: number;
  steam?: {
    steamid?: string;
    personaname?: string;
  };
}

const SearchBar = ({
  items,
  loading,
  onSelect,
  onFocus,
  debouncedSearch,
  placeholder,
  width = "",
  backgroundColor = "",
  hoverColor = "",
  useSearchSuggestions = false,
  populateOnClick = false,
  locationIdentifier = "",
}: {
  items: ISearchItem[];
  loading: boolean;
  onSelect: (item: ISearchItem) => void;
  onFocus: () => void;
  debouncedSearch: (query: string) => void;
  placeholder: string;
  width?: string;
  backgroundColor?: string;
  hoverColor?: string;
  suggestedUsers?: ISearchItem[];
  useSearchSuggestions?: boolean;
  populateOnClick?: boolean;
  locationIdentifier?: string;
}) => {
  const { suggestedUsers: matchHistoryUsers } = useSuggestedMatchHistoryUsers();
  const { suggestedUsers: searchUsers } = useUserSuggestionsForSearch();
  const suggestedUsers = useSearchSuggestions ? searchUsers : matchHistoryUsers;
  const allstarUser = useRecoilValue(AllstarUser);

  const [inputValue, setInputValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [, setShowMobile] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isOpen, setIsOpen] = useRecoilState(MatchHistorySearchBarIsOpenState);

  const isServerSearching = inputValue.trim().length >= 3;
  const query = inputValue.trim().toLowerCase();
  const isMatchHistoryWidget = locationIdentifier === "matchHistoryWidget";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setInputValue("");
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (debouncedSearchTimeoutRef.current) {
      const currentRef = debouncedSearchTimeoutRef.current;
      return () => clearTimeout(currentRef);
    }
  }, []);

  const scrollToSelectedItem = (index: number) => {
    const dropdownItems = dropdownRef.current?.querySelectorAll(
      "[data-dropdown-item]",
    );
    if (dropdownItems && dropdownItems[index]) {
      dropdownItems[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const searchResults = getSearchResults();

    if (!isOpen || !searchResults?.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev < searchResults.length - 1 ? prev + 1 : 0;
          scrollToSelectedItem(newIndex);
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : searchResults.length - 1;
          scrollToSelectedItem(newIndex);
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleSelect(searchResults[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelect = (item: ISearchItem) => {
    const followingIds = allstarUser?.followers?.following;
    const isFollowing = followingIds
      ? followingIds.map((x: { _id: string }) => x?._id)?.includes(item.id)
      : false;

    if (!query && item.reason) {
      analytics.track("MH User Search - Click", {
        user: item.id,
        reason: item.reason,
        search: "suggested_users",
        following: isFollowing,
      });
    } else if (!query && !item.reason) {
      analytics.track("MH User Search - Click", {
        user: item.id,
        reason: "following",
        search: "suggested_users",
        following: isFollowing,
      });
    } else if (query) {
      analytics.track("MH User Search - Click", {
        user: item.id,
        reason: "search",
        search: query,
        following: isFollowing,
      });
    }

    if (populateOnClick) {
      const displayName = item.steam?.personaname || item.name;
      setInputValue(displayName);
      setIsOpen(false);
      setShowMobile(false);
      setSelectedIndex(-1);
    } else {
      onSelect(item);
      setIsOpen(false);
      setShowMobile(false);
      setInputValue("");
      setSelectedIndex(-1);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setInputValue(value);
    setIsOpen(true);
    setSelectedIndex(-1);

    if (value.trim().length >= 3) {
      debouncedSearch(value);
    }
  };

  const handleFocus = () => {
    onFocus();
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const getSearchResults = (): ISearchItem[] => {
    if (!query) {
      return suggestedUsers;
    }

    if (!isServerSearching) {
      return suggestedUsers.filter((user) =>
        user.name.toLowerCase().includes(query),
      );
    }

    return items;
  };

  return (
    <>
      <Container
        ref={wrapperRef}
        $width={width}
        $isMatchHistoryWidget={isMatchHistoryWidget}
        className="search-bar"
      >
        <Flex alignItems="center" style={{ gap: 16 }}>
          <InputWrapper
            className="input-wrapper"
            isOpen={isOpen}
            $backgroundColor={backgroundColor}
            $hoverColor={hoverColor}
            $isMatchHistoryWidget={isMatchHistoryWidget}
          >
            <StyledSearch $locationIdentifier={isMatchHistoryWidget} />
            <StyledInput
              onChange={handleInputChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || "Search..."}
              ref={inputRef}
              type="text"
              value={inputValue}
              $locationIdentifier={isMatchHistoryWidget}
            />
          </InputWrapper>
        </Flex>
        <SearchContainer isOpen={isOpen}>
          <InputPlaceholder />
          <DropdownWrapper
            isVisible={isOpen}
            ref={dropdownRef}
            $locationIdentifier={locationIdentifier}
          >
            {(() => {
              const searchResults = getSearchResults();
              return searchResults?.length ? (
                searchResults.map((item: ISearchItem, idx: number) => (
                  <DropdownItem
                    key={item.id}
                    avatar={item.image}
                    username={item.name}
                    callback={() => handleSelect(item)}
                    reason={item.reason}
                    userGroup={item.userGroup}
                    rank={item.rank}
                    steam={item.steam}
                    isSelected={idx === selectedIndex}
                  />
                ))
              ) : (
                <NoResults
                  loading={isServerSearching && loading}
                  isMatchHistoryWidget={isMatchHistoryWidget}
                />
              );
            })()}
          </DropdownWrapper>
        </SearchContainer>
      </Container>
    </>
  );
};

const Container = styled(Box)<{
  $width: string;
  $isMatchHistoryWidget: boolean;
}>`
  cursor: pointer !important;
  position: relative;
  width: ${(props) => (props.$width ? props.$width : "380px")};
  z-index: 2;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    width: ${(props) => (props.$width ? props.$width : "300px")};
  }

  ${({ $isMatchHistoryWidget }) =>
    $isMatchHistoryWidget &&
    `
    margin-top: 4px;
  `}
`;

const InputPlaceholder = styled(Box)`
  height: 20px;
  margin-bottom: 10px;
`;

const SearchContainer = styled(Box)<{ isOpen: boolean }>`
  background-color: ${({ theme, isOpen }) =>
    isOpen ? theme.colors.ned : "transparent"};
  border-radius: 20px;
  box-shadow: ${({ isOpen }) =>
    isOpen ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "none"};
  left: 0;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  padding: ${({ isOpen }) => (isOpen ? "14px 0" : "0px")};
  position: absolute;
  top: 0px;
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  width: 100%;
  z-index: 3;
`;

const InputWrapper = styled(Flex)<{
  isOpen: boolean;
  $backgroundColor: string;
  $hoverColor: string;
  $isMatchHistoryWidget: boolean;
}>`
  align-items: center;
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor ? $backgroundColor : theme.colors.midnightAlpha50};
  border-radius: 20px;
  border: 2px solid
    ${({ isOpen, theme, $isMatchHistoryWidget }) =>
      isOpen || $isMatchHistoryWidget ? theme.colors.envy : "transparent"};
  height: 40px;
  justify-content: left;
  padding: 3px;
  position: relative;
  width: 100%;
  z-index: 4;

  &:hover {
    cursor: text;
    background-color: ${({ theme, $hoverColor }) =>
      $hoverColor ? $hoverColor : theme.colors.gray850};
  }

  &:focus-within {
    border: 2px solid ${({ theme }) => theme.colors.envy};
  }
`;

const StyledSearch = styled(BiSearch)<{ $locationIdentifier: boolean }>`
  border-radius: 20px;
  fill: ${({ theme }) => theme.colors.chalk};
  height: 24px;
  padding: 0 0 0 8px;
  width: 24px;

  ${({ $locationIdentifier }) =>
    $locationIdentifier &&
    `
    padding: 0 0 0 6px;
    width: 20px;
  `}
`;

const StyledInput = styled.input<{ $locationIdentifier: boolean }>`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 500;
  height: 100%;
  outline: none;
  padding-left: 12px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
  }

  ${({ $locationIdentifier }) =>
    $locationIdentifier &&
    `
    padding-left: 4px;
  `}
`;

const DropdownWrapper = styled(Box)<{
  isVisible: boolean;
  $locationIdentifier: string;
}>`
  background-color: ${({ theme }) => theme.colors.ned};
  border-radius: 12px;
  display: ${({ isVisible }) => (isVisible ? "block" : "none")};
  height: ${({ isVisible }) => (isVisible ? "auto" : 0)};
  max-height: ${({ $locationIdentifier }) =>
    $locationIdentifier ? "80px" : "250px"};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  overflow-y: auto;
  padding: 8px 8px 0 8px;
  transition: opacity 0.2s;
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    display: none;
    position: absolute;
    right: 0px;
    top: 0px;
    width: 12px;
    height: 100%;
    z-index: 3;
    background: #192832;
    border-left: 1px solid #20323f;
  }

  ::-webkit-scrollbar-thumb {
    position: relative;
    display: block;
    height: 20px;
    width: 10px;
    border-radius: 4px;
    margin-left: 2px;
    background-color: #253a49;
    cursor: pointer;
  }
`;

export { SearchBar };
